import { useCallback } from 'react';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { ApiClient } from '../helpers';
import urls, { constructUrl } from 'src/shared/urls';

const ACCOUNT_EXISTS_ERROR = 'Please login to continue with this email';
const RATE_LIMIT_ERROR = 'Too many requests attempted, try again later';
const UNKNOWN_ERROR = 'Unable to verify email address';

type ApiError = {
  response?: {
    status?: number;
  };
};

const QUERY_KEY = ['email-availability'] as const;

export class EmailAvailabilityError extends Error {
  constructor(public reason: 'account_exists' | 'rate_limited' | 'unknown', message: string, public email?: string) {
    super(message);
    this.name = 'EmailAvailabilityError';
  }
}

const normalizeEmailAvailabilityError = (error: unknown, email?: string): EmailAvailabilityError => {
  if (error instanceof EmailAvailabilityError) {
    return error;
  }

  if (error instanceof Error && error.message === ACCOUNT_EXISTS_ERROR) {
    return new EmailAvailabilityError('account_exists', ACCOUNT_EXISTS_ERROR, email);
  }

  const status = (error as ApiError).response?.status;
  if (status === 429) {
    return new EmailAvailabilityError('rate_limited', RATE_LIMIT_ERROR, email);
  }

  return new EmailAvailabilityError('unknown', UNKNOWN_ERROR, email);
};

const emailAvailabilityRequest = async (email: string): Promise<void> => {
  try {
    const { body } = await new ApiClient().post(constructUrl(urls.post.accountAvailable), {
      data: { email },
    });

    if (body.account) {
      throw new EmailAvailabilityError('account_exists', ACCOUNT_EXISTS_ERROR, email);
    }
  } catch (error: unknown) {
    throw normalizeEmailAvailabilityError(error, email);
  }
};

export const useEmailAvailability = () => {
  const queryClient = useQueryClient();
  const isValidating = useIsFetching({ queryKey: QUERY_KEY, exact: false }) > 0;

  const validateEmail = useCallback(
    async (email: string) => {
      await queryClient.fetchQuery({
        queryKey: [...QUERY_KEY, email],
        queryFn: () => emailAvailabilityRequest(email),
        staleTime: 0,
        cacheTime: 0,
        retry: false,
      });
    },
    [queryClient],
  );

  const reset = useCallback(() => {
    queryClient.cancelQueries({ queryKey: QUERY_KEY, exact: false });
    queryClient.removeQueries({ queryKey: QUERY_KEY, exact: false });
  }, [queryClient]);

  return {
    validateEmail,
    isValidating,
    reset,
  };
};

export type UseEmailAvailabilityResult = ReturnType<typeof useEmailAvailability>;
